import { useState, type ReactNode } from 'react'
import { FilterOutlined } from '@ant-design/icons'
import { Badge, Button, Drawer, Form, Grid, Space } from 'antd'
import { useTranslation } from 'react-i18next'

export interface FilterDrawerProps<T extends object> {
  /**
   * Topshirilgan qiymat. Panel OCHILGANDA qoralama aynan shundan olinadi.
   *
   * ⚠️ X-SIB da bu odatda `useTableUrlState` ning `state.filters` i, ya'ni
   * MANZILdagi holat: sahifa yangilansa ham, havola ulashilsa ham filtr
   * saqlanadi. qrhub'da bu oddiy `useState` edi va o'sha yerda uchala
   * xossa ham yo'q.
   */
  value: T
  /**
   * Qoralamani topshiradi.
   *
   * ⚠️ BU KOMPONENTDA CHAQIRILADIGAN YAGONA JOY — «Qo'llash» tugmasi.
   * Maydon o'zgarishiga bog'lansa har harfda/har tanlovda so'rov ketardi
   * va bu hozirgi bir qatorli paneldan YOMONROQ bo'lardi.
   */
  onApply: (next: T) => void
  /** «Tozalash» qoralamani qaytaradigan qiymat (odatda sukut filtr) */
  resetValue: T
  /**
   * Tugmadagi nishon — nechta filtr faol.
   *
   * ⚠️ Usiz yopiq panel ro'yxatni JIMGINA toraytirardi: operator qisqargan
   * ro'yxatni ko'rib "ma'lumot yo'q" degan xulosaga kelardi.
   */
  activeCount: number
  /** Maydonlarni QORALAMA ustida chizadi */
  children: (draft: T, patch: (partial: Partial<T>) => void) => ReactNode
  title?: string
}

const DESKTOP_WIDTH = 380

/**
 * O'ng tomondan ochiladigan filtr paneli.
 *
 * <h2>Nega kerak bo'ldi</h2>
 * Egasi qarori (2026-08-20): abonentlar ro'yxatida filtrlar ikki qatorga
 * bo'linib ketgan va yangi filtr qo'shilsa uchinchi qator paydo bo'lardi.
 * Naqsh qrhub panelidagi dinamik QR sahifasidan olindi.
 *
 * <h2>⚠️ QORALAMA — naqshning O'ZAGI</h2>
 * Tahrirlash mahalliy qoralamada ketadi va faqat «Qo'llash» da
 * topshiriladi. Ya'ni to'rtta maydonni to'ldirish serverga TO'RTTA emas,
 * BITTA so'rov yuboradi. Qoralamasiz bu panel filtr qatorining
 * yashirilgan ko'rinishi bo'lardi, xolos.
 *
 * <h2>⚠️ Panelni yopish — bekor qilish</h2>
 * Qoralama har OCHILISHDA `value` dan qayta olinadi, shuning uchun
 * «Qo'llash» siz yopilgan o'zgarishlar yo'qoladi va bu kutilgan holat.
 *
 * <h2>⚠️ Telefonda TO'LIQ kenglik</h2>
 * 380px li panel 390px li ekranda ostidagi jadvalning bir tomchisini
 * ochiq qoldirardi va tasodifiy bosish panelni yopib yuborardi.
 * qrhub'da kenglik qat'iy 380 — bu yerda ataylab boshqacha.
 */
export function FilterDrawer<T extends object>({
  value,
  onApply,
  resetValue,
  activeCount,
  children,
  title,
}: FilterDrawerProps<T>) {
  const { t } = useTranslation()
  // `AppLayout` va `AppHeader` bilan AYNI chegara
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<T>(value)

  /*
    ⚠️ Qoralama OCHISHDA olinadi, `useEffect` bilan EMAS.

    `[open, value]` ga bog'langan effekt `value` obyektining havolasi
    o'zgargan har renderda qoralamani tiklardi — ya'ni panel ochiq
    turganda kiritilgan qiymat sababsiz yo'qolardi. Ochish nuqtasi
    esa bitta va u aniq.
  */
  const openDrawer = () => {
    setDraft(value)
    setOpen(true)
  }

  const patchDraft = (partial: Partial<T>) => {
    setDraft((previous) => ({ ...previous, ...partial }))
  }

  const apply = () => {
    onApply(draft)
    setOpen(false)
  }

  return (
    <>
      <Badge count={activeCount} size="small">
        <Button icon={<FilterOutlined />} onClick={openDrawer}>
          {t('dataTable.filters')}
        </Button>
      </Badge>

      <Drawer
        title={title ?? t('dataTable.filters')}
        placement="right"
        width={isMobile ? '100%' : DESKTOP_WIDTH}
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={() => setDraft(resetValue)}>{t('dataTable.clearFilters')}</Button>
            <Button type="primary" onClick={apply}>
              {t('dataTable.applyFilters')}
            </Button>
          </Space>
        }
      >
        {/* `component="div"` — ichma-ich `<form>` va tasodifiy yuborish bo'lmasin */}
        <Form layout="vertical" component="div">
          {children(draft, patchDraft)}
        </Form>
      </Drawer>
    </>
  )
}
